import { useEffect, useState } from 'react';
import { CarteIncidents } from './CarteIncidents';
import { JournalCrise } from './JournalCrise';
import { PiecesJointes } from './PiecesJointes';
import { PrisePhoto, joindrePhoto } from './PrisePhoto';
import type { PhotoReduite } from '../../terrain/photos';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  INCIDENT_STATUS_LABELS,
  INCIDENT_STATUSES,
  PRIORITY_LABELS,
  REPORT_CATEGORIES,
  categoryLabel,
  type IncidentStatus,
  type Domain,
  type Priority,
} from '@eec/contrats';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';
import { useOrganisation } from '../../api/requetes';

interface Incident {
  id: string;
  reference: string;
  domaine: Domain;
  categorie: string;
  titre: string;
  description: string;
  priorite: Priority;
  statut: IncidentStatus;
  version: number;
  localisation: string;
  latitude: number | null;
  longitude: number | null;
  cree_le: string;
  historique: {
    id: string;
    statut_apres: IncidentStatus;
    commentaire: string;
    auteur_nom: string;
    cree_le: string;
  }[];
  transitions: { to: IncidentStatus; requiresComment?: boolean }[];
}
const styleFormulaire = { display: 'grid', gap: 12, maxWidth: 700 };

export function PageIncidentsReseau() {
  const [parametres] = useSearchParams();
  const { contexte } = useSession();
  const client = useQueryClient();
  const organisation = useOrganisation();
  const [recherche, setRecherche] = useState('');
  useEffect(() => {
    setRecherche(parametres.get('recherche') ?? '');
  }, [parametres]);
  const [statut, setStatut] = useState('');
  const [domaine, setDomaine] = useState<Domain>('WATER');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [creation, setCreation] = useState(false);
  const [photo, setPhoto] = useState<PhotoReduite | null>(null);
  const [preparationPhoto, setPreparationPhoto] = useState(false);
  const [photoEnAttente, setPhotoEnAttente] = useState<{ id: string; photo: PhotoReduite } | null>(
    null,
  );
  async function envoyerPhoto(pending: { id: string; photo: PhotoReduite }) {
    try {
      await joindrePhoto('incident', pending.id, pending.photo);
      setPhotoEnAttente(null);
      setErreur('');
    } catch (err) {
      setErreur(
        `Incident créé, mais photo non transmise : ${messageErreur(err)}. Restez sur cette page pour réessayer.`,
      );
    }
  }
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const liste = useQuery({
    queryKey: ['incidents-reseau', recherche, statut],
    queryFn: ({ signal }) =>
      requete<{ elements: Incident[] }>('/incidents', {
        signal,
        parametres: { recherche, ...(statut ? { statut } : {}) },
      }),
    enabled: peut(contexte, 'incident', 'voir'),
  });
  const provinces = useQuery({
    queryKey: ['provinces'],
    queryFn: () => requete<{ elements: { id: string; nom: string }[] }>('/referentiel/provinces'),
    enabled: creation,
  });
  const equipements = useQuery({
    queryKey: ['patrimoine', 'equipements'],
    queryFn: () =>
      requete<{ elements: { id: string; code: string; nom: string; domaine: Domain }[] }>(
        '/patrimoine/equipements',
      ),
    enabled: creation && peut(contexte, 'patrimoine', 'voir'),
  });
  if (!peut(contexte, 'incident', 'voir'))
    return <p role="alert">Vous ne disposez pas du droit de consulter les incidents.</p>;
  return (
    <section>
      <EnTetePage titre="Incidents des réseaux" />
      {peut(contexte, 'incident', 'creer') && (
        <button
          disabled={enCours || preparationPhoto || !!photoEnAttente}
          onClick={() => setCreation(!creation)}
        >
          {creation ? 'Fermer le formulaire' : 'Créer un incident'}
        </button>
      )}
      {creation && (
        <form
          style={styleFormulaire}
          onSubmit={async (e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const data = new FormData(form);
            setErreur('');
            setEnCours(true);
            try {
              const incident = await requete<{ id: string }>('/incidents', {
                methode: 'POST',
                corps: {
                  domaine,
                  categorie: data.get('categorie'),
                  titre: data.get('titre'),
                  description: data.get('description'),
                  priorite: data.get('priorite'),
                  province_id: data.get('province_id'),
                  equipement_id: data.get('equipement_id') || null,
                  localisation: data.get('localisation'),
                  latitude: latitude === '' ? null : Number(latitude),
                  longitude: longitude === '' ? null : Number(longitude),
                },
              });
              setCreation(false);
              setLatitude('');
              setLongitude('');
              if (photo) {
                const pending = { id: incident.id, photo };
                setPhotoEnAttente(pending);
                setPhoto(null);
                await envoyerPhoto(pending);
              }
              await client.invalidateQueries({ queryKey: ['incidents-reseau'] });
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setEnCours(false);
            }
          }}
        >
          <h2>Nouvel incident</h2>
          <label>
            Service{' '}
            <select value={domaine} onChange={(e) => setDomaine(e.target.value as Domain)}>
              <option value="WATER">Eau</option>
              <option value="ELECTRICITY">Électricité</option>
            </select>
          </label>
          <label>
            Catégorie{' '}
            <select name="categorie" key={domaine}>
              {REPORT_CATEGORIES[domaine].map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Titre <input name="titre" required minLength={5} maxLength={200} />
          </label>
          <label>
            Description <textarea name="description" maxLength={4000} />
          </label>
          <label>
            Priorité{' '}
            <select name="priorite" defaultValue="NORMAL">
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label>
            Province{' '}
            <select
              name="province_id"
              required
              defaultValue={organisation.data?.province?.id ?? ''}
            >
              <option value="">Choisir une province</option>
              {provinces.data?.elements.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nom}
                </option>
              ))}
            </select>
          </label>
          <label>
            Localisation{' '}
            <input
              name="localisation"
              required
              minLength={2}
              maxLength={250}
              placeholder="Ville, quartier, repère"
            />
          </label>
          <CarteIncidents
            incidents={
              latitude && longitude
                ? [
                    {
                      id: 'nouveau',
                      reference: '',
                      titre: 'Position choisie',
                      statut: 'NEW',
                      latitude: Number(latitude),
                      longitude: Number(longitude),
                    },
                  ]
                : []
            }
            onPosition={([lon, lat]) => {
              setLatitude(lat.toFixed(6));
              setLongitude(lon.toFixed(6));
            }}
          />
          <label>
            Latitude{' '}
            <input
              type="number"
              step="any"
              min={-4}
              max={2.5}
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              required
            />
          </label>
          <label>
            Longitude{' '}
            <input
              type="number"
              step="any"
              min={8}
              max={15}
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              required
            />
          </label>
          <button
            type="button"
            onClick={() => {
              if (!navigator.geolocation) {
                setErreur('La géolocalisation est indisponible sur cet appareil.');
                return;
              }
              navigator.geolocation.getCurrentPosition(
                (p) => {
                  setLatitude(p.coords.latitude.toFixed(6));
                  setLongitude(p.coords.longitude.toFixed(6));
                },
                () =>
                  setErreur(
                    'Position non obtenue. Cliquez sur la carte ou saisissez les coordonnées.',
                  ),
                { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
              );
            }}
          >
            Utiliser ma position actuelle
          </button>
          <p>
            La position de votre appareil est celle de la panne uniquement si vous êtes sur place.
            Vérifiez le point avant de valider.
          </p>
          {peut(contexte, 'patrimoine', 'voir') && (
            <label>
              Équipement concerné (facultatif){' '}
              <select name="equipement_id" key={`equipement-${domaine}`}>
                <option value="">Aucun équipement identifié</option>
                {equipements.data?.elements
                  .filter((e) => e.domaine === domaine)
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.code} — {e.nom}
                    </option>
                  ))}
              </select>
            </label>
          )}
          {equipements.error && <p role="alert">{messageErreur(equipements.error)}</p>}
          <PrisePhoto
            titre="Photo de l’incident"
            photo={photo}
            onChange={setPhoto}
            onBusy={setPreparationPhoto}
            disabled={enCours}
          />
          <button disabled={enCours || preparationPhoto || provinces.isPending} type="submit">
            {enCours ? 'Enregistrement…' : 'Enregistrer l’incident'}
          </button>
          {provinces.error && <p role="alert">{messageErreur(provinces.error)}</p>}
        </form>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      {photoEnAttente && (
        <div role="status">
          <p>L’incident est enregistré. Sa photo reste à envoyer ; ne fermez pas cette page.</p>
          <button
            disabled={enCours}
            onClick={async () => {
              setEnCours(true);
              try {
                await envoyerPhoto(photoEnAttente);
              } finally {
                setEnCours(false);
              }
            }}
          >
            Réessayer l’envoi de la photo
          </button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 16, marginBlock: 20, flexWrap: 'wrap' }}>
        <label>
          Rechercher{' '}
          <input value={recherche} maxLength={100} onChange={(e) => setRecherche(e.target.value)} />
        </label>
        <label>
          Statut{' '}
          <select value={statut} onChange={(e) => setStatut(e.target.value)}>
            <option value="">Tous les statuts</option>
            {INCIDENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {INCIDENT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {liste.isPending && <p role="status">Chargement des incidents…</p>}
      {liste.error && <p role="alert">{messageErreur(liste.error)}</p>}
      {liste.data && (
        <>
          {!creation && <CarteIncidents incidents={liste.data.elements} />}
          <p>
            {liste.data.elements.length} incident(s) affiché(s), au maximum les 100 plus récents.
            Affinez la recherche au besoin.
          </p>
          <ul className="eec-incidents-liste">
            {liste.data.elements.map((i) => (
              <li key={i.id} data-priorite={i.priorite} style={{ marginBlock: 12 }}>
                <Link to={`/incidents/${i.id}`}>{i.titre}</Link> —{' '}
                {INCIDENT_STATUS_LABELS[i.statut]} — {PRIORITY_LABELS[i.priorite]}
                <br />
                <small>
                  {i.localisation} · {i.reference}
                </small>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export function PageIncidentReseau() {
  const { contexte } = useSession();
  const { id } = useParams();
  const client = useQueryClient();
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const detail = useQuery({
    queryKey: ['incident-reseau', id],
    queryFn: ({ signal }) => requete<Incident>(`/incidents/${id}`, { signal }),
    enabled: Boolean(id),
  });
  if (detail.error) return <p role="alert">{messageErreur(detail.error)}</p>;
  const i = detail.data;
  if (!i) return <p role="status">Chargement…</p>;
  return (
    <section>
      <Link to="/incidents">Retour aux incidents</Link>
      <EnTetePage titre={i.titre} />
      <CarteIncidents incidents={[i]} />
      <PiecesJointes type="incident" id={i.id} ecriture={peut(contexte, 'incident', 'modifier')} />
      <p>
        {i.reference} · {INCIDENT_STATUS_LABELS[i.statut]} · {PRIORITY_LABELS[i.priorite]}
      </p>
      <p>
        {categoryLabel(i.categorie)} · {i.localisation}
      </p>
      <p style={{ whiteSpace: 'pre-wrap' }}>{i.description}</p>
      {i.priorite === 'CRITICAL' && <JournalCrise key={i.id} id={i.id} statut={i.statut} />}
      {i.transitions.length > 0 && (
        <form
          style={styleFormulaire}
          key={i.version}
          onSubmit={async (e) => {
            e.preventDefault();
            const data = new FormData(e.currentTarget);
            setEnCours(true);
            setErreur('');
            try {
              await requete(`/incidents/${id}/transitions`, {
                methode: 'POST',
                corps: {
                  statut: data.get('statut'),
                  commentaire: data.get('commentaire'),
                  version: i.version,
                },
              });
              await client.invalidateQueries({ queryKey: ['incident-reseau', id] });
              await client.invalidateQueries({ queryKey: ['incidents-reseau'] });
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setEnCours(false);
            }
          }}
        >
          <h2>Changer le statut</h2>
          <label>
            Nouveau statut{' '}
            <select name="statut">
              {i.transitions.map((t) => (
                <option key={t.to} value={t.to}>
                  {INCIDENT_STATUS_LABELS[t.to]}
                  {t.requiresComment ? ' (justification obligatoire)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            Justification <textarea name="commentaire" maxLength={4000} />
          </label>
          <button disabled={enCours}>Appliquer la transition</button>
        </form>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      <h2>Historique</h2>
      <ol>
        {i.historique.map((h) => (
          <li key={h.id}>
            {INCIDENT_STATUS_LABELS[h.statut_apres]} — {h.auteur_nom} —{' '}
            {new Date(h.cree_le).toLocaleString('fr-FR')}
            <p>{h.commentaire}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}
